import { useAppDispatch, useAppState } from "@/store/useApp";
import { Block } from "@/types/document";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { TableOfContentNode, resolveGroupedRoots, resolveHeaderRoots } from "./tocService";
import { TocNodeItem } from "./TocNodeItem";
import { ReadingTime } from "../../components/ReadingTime";

export const TableOfContent = () => {
  const document = useAppState((state) => state.currentDocument);
  const selectedGroup = useAppState((state) => state.selectedGroup);
  const selectedBlockId = useAppState((state) => state.selectedBlockId);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { documentId } = useParams();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Build table of contents with groups as H1 elements
  const tableOfContents = useMemo<TableOfContentNode[]>(() => {
    if (!document) return [];
    return document.groups.length > 1 ? resolveGroupedRoots(document) : resolveHeaderRoots(document);
  }, [document]);

  // Helper function to check if block is a header
  const isHeaderBlock = useCallback((block: Block): boolean => {
    return ["header1", "header2", "header3", "header4"].includes(block.type);
  }, []);

  // Find which section the currently selected block belongs to
  const findCurrentSection = useCallback((): string | null => {
    if (!selectedBlockId || !document) return null;

    // Get blocks and current block index based on document type
    const { blocks, currentBlockIndex, fallbackId } = document.groups.length > 1 && selectedGroup
      ? {
          blocks: selectedGroup.blockIds
            .map(blockId => document.blocks.find(b => b.id === blockId))
            .filter(Boolean) as Block[],
          currentBlockIndex: selectedGroup.blockIds.indexOf(selectedBlockId),
          fallbackId: selectedGroup.id
        }
      : {
          blocks: document.blocks,
          currentBlockIndex: document.blocks.findIndex(block => block.id === selectedBlockId),
          fallbackId: null
        };

    if (currentBlockIndex === -1) return null;

    // Find the last header that comes before or at the current block position
    let currentSectionHeader: Block | null = null;
    for (let i = 0; i <= currentBlockIndex; i++) {
      const block = blocks[i];
      if (block && isHeaderBlock(block)) {
        currentSectionHeader = block;
      }
    }

    return currentSectionHeader?.id || fallbackId;
  }, [document, selectedBlockId, selectedGroup, isHeaderBlock]);

  const currentSectionId = useMemo(() => findCurrentSection(), [findCurrentSection]);

  // Helper functions for node expansion
  const containsTargetRecursive = useCallback(function containsTargetRecursiveInner(
    node: TableOfContentNode,
    targetId: string,
  ): boolean {
    if (node.id === targetId) return true;
    return node.children.some(child => containsTargetRecursiveInner(child, targetId));
  }, []);

  const expandParentSections = useCallback(function expandParentSectionsInner(
    nodeId: string,
    nodes: TableOfContentNode[],
    expandedSet: Set<string>,
  ) {
    for (const node of nodes) {
      // Check if this node or its children contain the target
      const containsTarget = containsTargetRecursive(node, nodeId);

      if (containsTarget) {
        expandedSet.add(node.id);
        // Recursively expand children if needed
        if (node.children.length > 0) {
          expandParentSectionsInner(nodeId, node.children, expandedSet);
        }
      }
    }
  }, [containsTargetRecursive]);

  // Automatically expand the selected group and current section
  useEffect(() => {
    if (!document) return;
    
    setExpandedNodes(prevExpanded => {
      const newExpanded = new Set(prevExpanded);
      
      // For grouped documents, expand the selected group
      if (document.groups.length > 1 && selectedGroup) {
        newExpanded.add(selectedGroup.id);
      }
      
      // Expand the section containing the current block and its parents
      if (currentSectionId) {
        newExpanded.add(currentSectionId);
        
        // Only expand parents if current section is not the top-level group
        if (document.groups.length > 1 || currentSectionId !== selectedGroup?.id) {
          expandParentSections(currentSectionId, tableOfContents, newExpanded);
        }
      }
      
      return newExpanded;
    });
  }, [selectedGroup, currentSectionId, tableOfContents, document, expandParentSections]);

  if (!document) return null;

  const toggleExpanded = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleNodeClick = (node: TableOfContentNode) => {
    // Ensure we're in reader view
    if (!location.pathname.endsWith(`/documents/${documentId}`)) {
      navigate(`/documents/${documentId}`);
    }

    if (document.groups.length > 1) {
      // For grouped documents, ensure we have a group
      if (!node.group) return;

      // Set the selected group
      dispatch({ type: "SET_SELECTED_GROUP", payload: node.group });

      // Determine block index within the group
      const blockIndex = node.block 
        ? node.group.blockIds.indexOf(node.block.id)
        : 0; // Default to first block if no specific block

      dispatch({ type: "SET_SELECTED_BLOCK_INDEX", payload: blockIndex });
    } else {
      // For non-grouped documents, handle block selection directly
      if (!node.block) return;

      // Find the block index in the document
      const blockIndex = document.blocks.findIndex(block => block.id === node.block!.id);
      if (blockIndex !== -1) {
        dispatch({ type: "SET_SELECTED_BLOCK_INDEX", payload: blockIndex });
      }
    }
  };

  if (tableOfContents.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
        No table of content
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-1">
        {tableOfContents.map((node) => (
          <TocNodeItem 
            key={node.id} 
            node={node}
            expandedNodes={expandedNodes}
            currentSectionId={currentSectionId}
            onNodeClick={handleNodeClick}
            onToggleExpanded={toggleExpanded}
          />
        ))}
      </div>
      
      <div className="pt-4 mt-4 border-t border-gray-200">
        <ReadingTime 
          layout="vertical"
          currentSectionId={currentSectionId}
          tableOfContents={tableOfContents}
        />
      </div>
    </div>
  );
};
